import React, { useState } from "react";
import {
    Button,
    Col,
    InputNumber,
    Modal,
    Row,
    Space,
    Typography,
} from "antd";
import {
    CalculatorOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";
import { getDenominacionesConfig } from "./configs/denominaciones.config";
import { useConfig } from "../../contexts/ConfigContext";
import { formatNumberByCountry } from "../../utils/formatNumbers";

const DenominacionesCalculator = ({
    value = 0,
    onChange,
    modalTitle = "Cálculo de Importe por Denominaciones",
    buttonTitle = "Calcular",
    disabled = false,
    placeholder = "Importe calculado",
}) => {
    const { country, loading } = useConfig();
    const [modalVisible, setModalVisible] = useState(false);
    const [modalConfirmVisible, setModalConfirmVisible] = useState(false);
    const [importePendiente, setImportePendiente] = useState(0);
    const [denominaciones, setDenominaciones] = useState({});
    const [totalCalculado, setTotalCalculado] = useState(0);

    if (loading || !country) {
        return null;
    }

    const config = getDenominacionesConfig(country?.code);
    const { currencySymbol, bills, coins, defaultValues, expectedDefaultTotal, useDefaultValues } = config;

    const actualizarDenominacion = (valor, cantidad) => {
        setDenominaciones((prev) => {
            const nuevas = { ...prev, [valor]: cantidad };
            const nuevoTotal = Object.entries(nuevas).reduce(
                (sum, [v, c]) => sum + parseFloat(v) * c,
                0
            );
            setTotalCalculado(nuevoTotal);
            return nuevas;
        });
    };

    const establecerValoresPredeterminados = () => {
        setDenominaciones(defaultValues);
        const total = Object.entries(defaultValues).reduce(
            (sum, [v, c]) => sum + parseFloat(v) * c,
            0
        );
        setTotalCalculado(total);
    };

    const limpiarDenominaciones = () => {
        const vacio = {};
        [...bills, ...coins].forEach((v) => (vacio[v] = 0));
        setDenominaciones(vacio);
        setTotalCalculado(0);
    };

    const abrirModal = () => {
        useDefaultValues ? establecerValoresPredeterminados() : limpiarDenominaciones();
        setModalVisible(true);
    };

    const confirmarDenominaciones = () => {
        // Redondear el valor a 2 decimales para evitar errores de precisión
        const importeRedondeado = Math.round(totalCalculado * 100) / 100;

        // Solo verificar si el importe es diferente de 99.5 cuando se usan valores por defecto
        if (useDefaultValues && importeRedondeado !== expectedDefaultTotal) {
            setImportePendiente(importeRedondeado);
            setModalConfirmVisible(true);
        } else {
            // Si no se usan valores por defecto o es exactamente 99.5, proceder directamente
            procesarConfirmacion(importeRedondeado);
        }
    };

    const procesarConfirmacion = (importe) => {
        // Llamar al callback con el nuevo valor
        onChange?.(importe);
        window.messageApi?.success(`Importe establecido: ${formatNumberByCountry(importe, country, true)}`);
        setModalVisible(false);
    };

    return (
        <>
            <Space.Compact style={{ width: "100%" }}>
                <InputNumber
                    style={{ width: "85%" }}
                    placeholder={placeholder}
                    min={0}
                    step={0.01}
                    precision={2}
                    value={formatNumberByCountry(value, country, true)}
                    disabled
                />
                <Button
                    type="primary"
                    icon={<CalculatorOutlined />}
                    onClick={abrirModal}
                    disabled={disabled}
                    style={{ width: "15%" }}
                    title={buttonTitle}
                />
            </Space.Compact>

            <Modal
                title={modalTitle}
                open={modalVisible}
                onOk={confirmarDenominaciones}
                onCancel={() => setModalVisible(false)}
                width={600}
                footer={[
                    <Button key="cancel" onClick={() => setModalVisible(false)}>
                        Cancelar
                    </Button>,
                    <Button key="submit" type="primary" onClick={confirmarDenominaciones}>
                        Confirmar Importe
                    </Button>,
                ]}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        Ingrese la cantidad de billetes y monedas:
                    </Typography.Title>
                    {useDefaultValues &&
                        <Button type="default" size="small" onClick={establecerValoresPredeterminados}>
                            Valores por defecto ({currencySymbol}{expectedDefaultTotal})
                        </Button>
                    }
                </div>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Typography.Title level={5} style={{ textAlign: "center", marginBottom: 16 }}>
                            Billetes
                        </Typography.Title>
                        {bills.map((valor) => (
                            <Space.Compact key={valor} style={{ width: "100%", marginBottom: 8 }}>
                                <Typography.Text style={{ minWidth: "110px", textAlign: "right", paddingRight: 8 }}>
                                    {formatNumberByCountry(valor, country, true)}:
                                </Typography.Text>
                                <InputNumber
                                    style={{ width: "100%" }}
                                    min={0}
                                    value={denominaciones[valor] || 0}
                                    onChange={(v) => actualizarDenominacion(valor, v || 0)}
                                    placeholder="Cantidad"
                                />
                            </Space.Compact>
                        ))}
                    </Col>
                    <Col span={12}>
                        <Typography.Title level={5} style={{ textAlign: "center", marginBottom: 16 }}>
                            Monedas
                        </Typography.Title>
                        {coins.map((valor) => (
                            <Space.Compact key={valor} style={{ width: "100%", marginBottom: 8 }}>
                                <Typography.Text style={{ minWidth: "90px", textAlign: "right", paddingRight: 8 }}>
                                    {formatNumberByCountry(valor, country, true)}:
                                </Typography.Text>
                                <InputNumber
                                    style={{ width: "100%" }}
                                    min={0}
                                    value={denominaciones[valor] || 0}
                                    onChange={(v) => actualizarDenominacion(valor, v || 0)}
                                    placeholder="Cantidad"
                                />
                            </Space.Compact>
                        ))}
                    </Col>
                </Row>

                <Typography.Title level={4} style={{ marginTop: 24, textAlign: "right" }}>
                    Total: {formatNumberByCountry(totalCalculado, country, true)}
                </Typography.Title>
            </Modal>

            <Modal
                title="Confirmación de Importe"
                open={modalConfirmVisible}
                onOk={() => procesarConfirmacion(importePendiente)}
                onCancel={() => setModalConfirmVisible(false)}
                width={500}
                okText="Aceptar"
                cancelText="Cancelar"
            >
                <p>
                    <ExclamationCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />
                    El importe calculado es <strong>{formatNumberByCountry(importePendiente, country, true)}</strong>
                </p>
                <p>Este importe no es {formatNumberByCountry(expectedDefaultTotal, country, true)} como se esperaba.</p>
                <p>¿Desea continuar con este importe?</p>
            </Modal>
        </>
    );
};

export default DenominacionesCalculator;